import React, { useState } from 'react';
import { useQuery, useSubscription, gql } from '@apollo/client';
import { Box, Typography, makeStyles, Theme, IconButton, Button, Popover, Tabs, Tab } from '@material-ui/core';
import { RouteComponentProps } from 'react-router';
import LoadingIndicator from '@/components/core/LoadingIndicator';
import { staffLogItem } from '../../../graph/staffLog';
import { item as physicianItem } from '../../../graph/physicians';
import { item as anesthesiologistItem } from '../../../graph/anesthesiologists';
import { listSubscription } from '../../../graph/rooms';
import clsx from 'clsx';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import StaffLocationHistory from './StaffLocationHistory';
import ListAltIcon from '@material-ui/icons/ListAlt';
import TimelineIcon from '@material-ui/icons/Timeline';
import DonutLargeIcon from '@material-ui/icons/DonutLarge';
import { useScope } from '@/hooks/useScope';
import get from 'lodash/get';
import TimelineGraph from './TimelineGraph';
import StatsPieChart from './StatsPieChart';
import { Calendar } from 'react-date-range';
import { formatDollars, roundCents } from '@/util/money';

const STAFF_MONITORING_BY_ID = gql`
  query staffMonitoringById($date: Date!, $id: Long!, $role: String!) {
    staffMonitoringById(date: $date, id: $id, role: $role) {
      entries {
        roomId
        entityId
        setAt
        room {
          id
          name
          type
        }
      }
    }
  }
`;

const STAFF_MONITORING_BY_ID_SUBSCRIPTION = gql`
  subscription staffMonitoringByIdSubscription($date: Date!, $id: Long!, $role: String!) {
    staffMonitoringById(date: $date, id: $id, role: $role) {
      entries {
        roomId
        entityId
        setAt
        room {
          id
          name
          type
        }
      }
    }
  }
`;

function createLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateDisplay(dateString: string): string {
  const date = createLocalDate(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface RouteParams {
  id: string;
  role: string;
  date: string;
  organizationId: string;
}

interface Props extends RouteComponentProps<RouteParams> {}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3),
    minHeight: '100vh',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    fontSize: '32px',
    fontWeight: 500,
  },
  headerStats: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: '#4CAF50',
    fontSize: '14px',
    fontWeight: 500,
    '& .pulse': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: '#4CAF50',
      animation: '$pulse 2s infinite',
    },
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)',
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 10px rgba(76, 175, 80, 0)',
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)',
    },
  },
  statBox: {
    background: 'rgba(255, 255, 255, 0.08)',
    borderRadius: 8,
    padding: theme.spacing(2, 3),
    minWidth: '140px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
  },
  statLabel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '12px',
    marginBottom: theme.spacing(0.5),
    fontWeight: 400,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  statValue: {
    color: theme.palette.common.white,
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 1.2,
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(2),
    marginLeft: 'auto',
    alignItems: 'center',
  },
  showLogsButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    color: theme.palette.common.white,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2.5),
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    minHeight: '40px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1),
    },
  },
  dateButton: {
    backgroundColor: '#3B9EFF',
    color: theme.palette.common.white,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2.5),
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    minHeight: '40px',
    '&:hover': {
      backgroundColor: '#2B8EEF',
    },
  },
  customTabs: {
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    '& .MuiTab-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      color: 'rgba(255, 255, 255, 0.7)',
      textTransform: 'none',
      fontSize: '14px',
      fontWeight: 500,
      minHeight: '40px',
      padding: theme.spacing(1.5, 2.5),
      borderRadius: 8,
      border: '1px solid rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      marginRight: theme.spacing(1),
      minWidth: 'auto',
      '&.Mui-selected': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        color: theme.palette.common.white,
        border: '1px solid rgba(255, 255, 255, 0.2)',
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
        color: theme.palette.common.white,
      },
    },
  },

  mainWrapper: {
    width: '100%',
    display: 'grid',
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      border: 'none',
      gap: theme.spacing(2),
    },
  },
  mainWrapperLg: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    alignSelf: 'flex-start',
  },
  mainWrapperSm: {
    gridTemplateColumns: '1fr 1fr',
  },
  mainWrapperXs: {
    gridTemplateColumns: '1fr',
  },
  dataField: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
      border: 0,
    },
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
  },
  value: {
    fontSize: '0.875rem',
  },
  primarySpecialty: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& .dot': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
    },
  },
  calendarLink: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  infoLabel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '14px',
    fontWeight: 400,
  },
  infoValue: {
    color: theme.palette.common.white,
    fontSize: '16px',
    fontWeight: 400,
    '&.email': {
      color: '#3B9EFF',
    },
    '&.dash': {
      color: 'rgba(255, 255, 255, 0.6)',
    },
  },
  timelineContainer: {
    marginTop: theme.spacing(4),
    width: '100%',
    minHeight: 500,
    background: 'transparent',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
  },
  timelineGrid: {
    display: 'grid',
    gridTemplateColumns: '100px 1fr',
    gap: theme.spacing(1),
    flex: 1,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 100,
      right: 0,
      bottom: 0,
      backgroundImage: `
        linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: 'calc((100% - 1px) / 12) 28px',
      backgroundPosition: '0 0',
      pointerEvents: 'none',
    },
  },
  roomLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '13px',
    fontWeight: 400,
    textAlign: 'right',
    paddingRight: theme.spacing(2),
    lineHeight: '28px',
    whiteSpace: 'nowrap',
  },
  timelineRow: {
    position: 'relative',
    height: 28,
    backgroundColor: 'transparent',
    marginBottom: theme.spacing(1.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  timeBlock: {
    position: 'absolute',
    height: '100%',
    borderRadius: 4,
    boxShadow: '0 0 8px rgba(0, 0, 0, 0.2)',
    '&.or': {
      backgroundColor: 'rgba(124, 179, 66, 0.9)',
    },
    '&.other': {
      backgroundColor: 'rgba(255, 152, 0, 0.9)',
    },
    '&.pacu': {
      backgroundColor: 'rgba(92, 107, 192, 0.9)',
    },
    '&.break': {
      backgroundColor: 'rgba(120, 144, 156, 0.9)',
    },
    '&.waiting': {
      backgroundColor: 'rgba(92, 107, 192, 0.9)',
    },
    '&.preop': {
      backgroundColor: 'rgba(92, 107, 192, 0.9)',
    },
  },
  timeAxis: {
    position: 'relative',
    marginLeft: 100,
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  },
  timeLabels: {
    display: 'grid',
    gridTemplateColumns: 'repeat(12, 1fr)',
  },
  timeLabel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '12px',
    textAlign: 'center',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -10,
      left: 0,
      width: 1,
      height: 4,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  statsContainer: {
    marginTop: theme.spacing(4),
  },
  progressBar: {
    height: 4,
    display: 'flex',
    marginBottom: theme.spacing(2),
    '& > div': {
      height: '100%',
      transition: 'width 0.3s ease',
      '&.waiting': { backgroundColor: 'rgba(92, 107, 192, 0.9)' },
      '&.break': { backgroundColor: 'rgba(120, 144, 156, 0.9)' },
      '&.or': { backgroundColor: 'rgba(124, 179, 66, 0.9)' },
      '&.pacu': { backgroundColor: 'rgba(92, 107, 192, 0.9)' },
      '&.other': { backgroundColor: 'rgba(255, 152, 0, 0.9)' },
    },
  },
  statsBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    '& .dot': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      marginRight: theme.spacing(1),
    },
    '& .label': {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '13px',
      marginRight: theme.spacing(1),
    },
    '& .value': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      '& .percentage': {
        color: '#fff',
        fontSize: '13px',
        fontWeight: 500,
      },
      '& .time': {
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '13px',
      },
      '& .cost': {
        color: '#fff',
        fontSize: '13px',
        marginLeft: theme.spacing(0.5),
      },
    },
  },
}));

const InfoField = ({ label, value, type = 'text' }: { label: string; value: React.ReactNode; type?: string }) => {
  const classes = useStyles();
  return (
    <div className={classes.infoItem}>
      <Typography className={classes.infoLabel}>{label}</Typography>
      <Typography
        className={clsx(classes.infoValue, {
          email: type === 'email',
          dash: value === '-',
        })}
      >
        {value || '-'}
      </Typography>
    </div>
  );
};

interface TimeBlock {
  start: number;
  width: number;
  duration: number;
  enteredAt: string;
  exitedAt?: string;
  isCurrentlyInLocation: boolean;
}

const StaffLogPage: React.FC<Props> = ({ match, history }) => {
  const classes = useStyles();
  const [currentView, setCurrentView] = useState<'timeline' | 'logs' | 'piechart'>('timeline');
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  React.useEffect(() => {
    const interval = setInterval(
      () => {
        setCurrentTime(new Date());
      },
      1 * 60 * 1000
    );

    return () => clearInterval(interval);
  }, []);

  const { id, role, date } = match.params;
  const scope = useScope();
  const hospitalTimezone = get(scope, 'hospital.timezone.id');

  const { data: roomsData } = useSubscription(listSubscription);

  const handleBack = () => {
    const organizationId = match.params.organizationId;
    history.push(`/kiosk/${organizationId}/monitor-staff?date=${date}`);
  };

  const today = formatLocalDate(new Date());
  const isCurrentDay = date === today;

  const subscription = useSubscription(STAFF_MONITORING_BY_ID_SUBSCRIPTION, {
    variables: {
      date,
      id: parseInt(id, 10),
      role: role.toUpperCase(),
    },
    skip: !isCurrentDay,
  });

  const query = useQuery(STAFF_MONITORING_BY_ID, {
    variables: {
      date,
      id: parseInt(id, 10),
      role: role.toUpperCase(),
    },
    skip: isCurrentDay,
  });

  const {
    loading: monitoringLoading,
    error: monitoringError,
    data: monitoringData,
  } = isCurrentDay ? subscription : query;

  const {
    data: staffData,
    loading: staffLoading,
    error: staffError,
  } = useQuery(staffLogItem, {
    variables: {
      id: parseInt(id, 10),
      role: role.toUpperCase(),
      date,
    },
    skip: role.toLowerCase() !== 'staff',
  });

  const {
    data: physicianData,
    loading: physicianLoading,
    error: physicianError,
  } = useQuery(physicianItem, {
    variables: {
      id: parseInt(id, 10),
    },
    skip: role.toLowerCase() !== 'physician',
  });

  const {
    data: anesthesiologistData,
    loading: anesthesiologistLoading,
    error: anesthesiologistError,
  } = useQuery(anesthesiologistItem, {
    variables: {
      id: parseInt(id, 10),
    },
    skip: role.toLowerCase() !== 'anesthesiologist',
  });

  if (staffLoading || physicianLoading || anesthesiologistLoading || monitoringLoading) return <LoadingIndicator />;
  if (staffError || physicianError || anesthesiologistError || monitoringError)
    return (
      <Typography color="error">
        Error loading details:{' '}
        {staffError?.message || physicianError?.message || anesthesiologistError?.message || monitoringError?.message}
      </Typography>
    );

  const person = {
    ...staffData?.staffMemberIncludingVoided,
    ...physicianData?.physician,
    ...anesthesiologistData?.anesthesiologist,
  };

  const formatDateTime = (dateTimeStr: string | null) => {
    if (!dateTimeStr) return '-';
    const date = new Date(dateTimeStr);
    const localTime = date.toLocaleString('en-US', {
      timeZone: hospitalTimezone || 'UTC',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });

    const [time, period] = localTime.split(' ');
    const [hours, minutes] = time.split(':');
    const formattedTime = `${hours}:${minutes.padStart(2, '0')} ${period}`;
    return formattedTime;
  };

  const calculateDuration = (startTime: string | null, endTime: string | null) => {
    if (!startTime || !endTime) return '-';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const duration = end.getTime() - start.getTime();
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const calculateCost = (hourlyRate: number | null, startTime: string | null, endTime: string | null) => {
    if (!hourlyRate || !startTime || !endTime) return 0;
    const start = new Date(startTime);
    const end = new Date(endTime);
    const duration = end.getTime() - start.getTime();
    const durationInStartedMinutes = Math.ceil(duration / (1000 * 60));
    return roundCents((hourlyRate * durationInStartedMinutes) / 60);
  };

  const renderTimeline = () => {
    const entries = monitoringData?.staffMonitoringById?.entries || [];
    const sortedEntries = [...entries].sort((a, b) => {
      return new Date(a.setAt).getTime() - new Date(b.setAt).getTime();
    });

    const calculateTimeRange = () => {
      if (sortedEntries.length === 0) {
        return { start: 6, end: 18 };
      }

      const formatTimeInHospitalTZ = (date: Date) => {
        return date.toLocaleString('en-US', {
          timeZone: hospitalTimezone || 'UTC',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false,
        });
      };

      const parseTime = (timeStr: string) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours + minutes / 60;
      };

      const allTimes = sortedEntries.map(entry => {
        const time = new Date(entry.setAt);
        const timeLocal = formatTimeInHospitalTZ(time);
        return parseTime(timeLocal);
      });

      const minTime = Math.min(...allTimes);
      const maxTime = Math.max(...allTimes);

      const isStillOnSite = () => {
        if (sortedEntries.length === 0) return false;
        const lastEntry = sortedEntries[sortedEntries.length - 1];
        if (!lastEntry?.setAt) return false;

        if (!lastEntry.room) return false;

        const lastEntryDate = new Date(lastEntry.setAt);
        const today = new Date();

        const lastEntryInHospitalTZ = new Date(
          lastEntryDate.toLocaleString('en-US', { timeZone: hospitalTimezone || 'UTC' })
        );
        const todayInHospitalTZ = new Date(today.toLocaleString('en-US', { timeZone: hospitalTimezone || 'UTC' }));

        return lastEntryInHospitalTZ.toDateString() === todayInHospitalTZ.toDateString();
      };

      const currentTimeInHospitalTZ = currentTime.toLocaleString('en-US', {
        timeZone: hospitalTimezone || 'UTC',
        hour: 'numeric',
        minute: 'numeric',
        hour12: false,
      });
      const currentHour =
        parseFloat(currentTimeInHospitalTZ.split(':')[0]) + parseFloat(currentTimeInHospitalTZ.split(':')[1]) / 60;

      let startHour = Math.max(0, Math.floor(minTime));
      let endHour = Math.min(24, Math.ceil(isStillOnSite() ? Math.max(maxTime, currentHour) : maxTime));

      if (endHour - startHour < 2) {
        endHour = startHour + 2;
      }

      return { start: startHour, end: endHour };
    };

    const timeRange = calculateTimeRange();

    const allRooms = roomsData?.internalMonitor || [];
    const filteredRooms = allRooms.filter(room => room.type !== 'ANESTHESIOLOGIST');

    const sortedRooms = [...filteredRooms].sort((a, b) => {
      const aIsOR = a.name.startsWith('OR');
      const bIsOR = b.name.startsWith('OR');
      if (aIsOR && bIsOR) {
        const aNum = parseInt(a.name.replace('OR', ''));
        const bNum = parseInt(b.name.replace('OR', ''));
        return aNum - bNum;
      }

      const roomTypeOrder = {
        WAITING_ROOM: 1,
        PRE_OP: 2,
        OR: 3,
        OPERATING_ROOM: 3,
        PACU: 4,
        POST_OP: 5,
        POSTOP: 5,
        VIRTUAL: 6,
        UNKNOWN: 7,
        OFF_SITE: 7,
      };

      if (aIsOR && !bIsOR) {
        const bOrder = roomTypeOrder[b.type] || 99;
        if (bOrder <= 2) return 1;
        return -1;
      }
      if (!aIsOR && bIsOR) {
        const aOrder = roomTypeOrder[a.type] || 99;
        if (aOrder <= 2) return -1;
        return 1;
      }

      const aOrder = roomTypeOrder[a.type] || 99;
      const bOrder = roomTypeOrder[b.type] || 99;
      if (aOrder !== bOrder) return aOrder - bOrder;

      return a.name.localeCompare(b.name);
    });

    const unknownRoom = {
      id: -1,
      name: 'Off Site',
      type: 'UNKNOWN',
    };
    sortedRooms.push(unknownRoom);

    const timeBlocks = sortedRooms.map(room => {
      const roomEntries =
        room.id === -1
          ? sortedEntries.filter(entry => !entry.room)
          : sortedEntries.filter(entry => entry.room?.id === room.id);

      const blocks = roomEntries
        .map((entry, index) => {
          const startTime = new Date(entry.setAt);
          const entryIndex = sortedEntries.findIndex(e => e === entry);
          const nextEntry = sortedEntries[entryIndex + 1];

          const isLastEntry = entryIndex === sortedEntries.length - 1;
          const isStillOnSite = () => {
            if (!entry.setAt) return false;
            const entryDate = new Date(entry.setAt);
            const today = new Date();

            const entryInHospitalTZ = new Date(
              entryDate.toLocaleString('en-US', { timeZone: hospitalTimezone || 'UTC' })
            );
            const todayInHospitalTZ = new Date(today.toLocaleString('en-US', { timeZone: hospitalTimezone || 'UTC' }));

            if (!entry.room) return false;

            return entryInHospitalTZ.toDateString() === todayInHospitalTZ.toDateString();
          };

          const endTime =
            isLastEntry && isStillOnSite()
              ? currentTime
              : nextEntry
                ? new Date(nextEntry.setAt)
                : new Date(entry.setAt);

          const formatTimeInHospitalTZ = (date: Date) => {
            return date.toLocaleString('en-US', {
              timeZone: hospitalTimezone || 'UTC',
              hour: 'numeric',
              minute: 'numeric',
              hour12: false,
            });
          };

          const startTimeLocal = formatTimeInHospitalTZ(startTime);
          const endTimeLocal = formatTimeInHospitalTZ(endTime);

          const parseTime = (timeStr: string) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            return hours + minutes / 60;
          };

          const startHour = parseTime(startTimeLocal);
          const endHour = parseTime(endTimeLocal);

          const dayStart = timeRange.start;
          const dayEnd = timeRange.end;
          const dayLength = dayEnd - dayStart;

          const clampedStartHour = Math.max(dayStart, Math.min(dayEnd, startHour));
          const clampedEndHour = Math.max(dayStart, Math.min(dayEnd, endHour));

          const startPercent = ((clampedStartHour - dayStart) / dayLength) * 100;
          const endPercent = ((clampedEndHour - dayStart) / dayLength) * 100;

          const width = Math.max(0, Math.min(100, endPercent - startPercent));

          if (width <= 0) {
            return false;
          }

          const isCurrentlyInLocation = isLastEntry && isStillOnSite();

          const block: TimeBlock = {
            start: startPercent,
            width: width,
            duration: Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60)),
            enteredAt: startTimeLocal,
            exitedAt: isCurrentlyInLocation ? undefined : endTimeLocal,
            isCurrentlyInLocation,
          };

          return block;
        })
        .filter((block): block is TimeBlock => Boolean(block));

      return {
        room: room.name,
        type: room.type,
        blocks,
      };
    });

    const calculateStats = () => {
      const isStillOnSite = () => {
        if (sortedEntries.length === 0) return false;
        const lastEntry = sortedEntries[sortedEntries.length - 1];
        if (!lastEntry?.setAt) return false;

        if (!lastEntry.room) return false;

        const lastEntryDate = new Date(lastEntry.setAt);
        const today = new Date();

        const lastEntryInHospitalTZ = new Date(
          lastEntryDate.toLocaleString('en-US', { timeZone: hospitalTimezone || 'UTC' })
        );
        const todayInHospitalTZ = new Date(today.toLocaleString('en-US', { timeZone: hospitalTimezone || 'UTC' }));

        return lastEntryInHospitalTZ.toDateString() === todayInHospitalTZ.toDateString();
      };

      const totalTime =
        sortedEntries.length > 0
          ? (isStillOnSite()
              ? currentTime.getTime()
              : new Date(sortedEntries[sortedEntries.length - 1].setAt).getTime()) -
            new Date(sortedEntries[0].setAt).getTime()
          : 0;

      const roomTimes: Record<string, { time: number; type: string }> = {};

      sortedEntries.forEach((entry, index) => {
        const roomName = entry.room?.name || 'Off Site';
        const roomType = entry.room?.type || 'UNKNOWN';

        if (!roomTimes[roomName]) {
          roomTimes[roomName] = { time: 0, type: roomType };
        }

        const isLastEntry = index === sortedEntries.length - 1;
        const endTime =
          isLastEntry && isStillOnSite()
            ? currentTime.getTime()
            : sortedEntries[index + 1]
              ? new Date(sortedEntries[index + 1].setAt).getTime()
              : new Date(entry.setAt).getTime();
        const startTime = new Date(entry.setAt).getTime();
        roomTimes[roomName].time += endTime - startTime;
      });

      return Object.entries(roomTimes).map(([roomName, { time, type }]) => ({
        roomName,
        type,
        percentage: Math.round((time / totalTime) * 100),
        duration: Math.round(time / (1000 * 60)),
        cost: roundCents((time / (1000 * 60 * 60)) * (person?.hourlyRate || 0)),
      }));
    };

    const stats = calculateStats();

    if (currentView === 'logs') {
      return (
        <Box mt={4}>
          <StaffLocationHistory entries={monitoringData?.staffMonitoringById?.entries || []} />
        </Box>
      );
    }

    if (currentView === 'piechart') {
      return <StatsPieChart stats={stats} />;
    }

    return <TimelineGraph timeBlocks={timeBlocks} stats={stats} timeRange={timeRange} />;
  };

  const renderStaffInfo = () => {
    const commonFields = [
      { label: 'Email', value: person?.email, type: 'email' },
      { label: 'Provider ID', value: person?.hstId },
    ];

    const roleSpecificFields = {
      staff: [
        { label: 'Phone Number', value: person?.phoneNumber },
        { label: 'Primary Specialty', value: 'OR' },
        { label: 'Other Specialties', value: person?.otherSpecialties?.join(', ') },
        { label: 'Title', value: person?.title },
        { label: 'Employment', value: person?.employment },
        { label: 'Compensation', value: person?.compensation },
        { label: 'Hourly Rate', value: person?.hourlyRate ? formatDollars(person.hourlyRate) : null },
      ],
      physician: [
        { label: 'Phone Number', value: person?.notificationNumbers },
        { label: 'Specialty', value: person?.speciality },
        { label: 'Category', value: person?.category },
        { label: 'Schedule Access', value: person?.hasScheduleAccess ? 'Yes' : 'No' },
      ],
      anesthesiologist: [
        { label: 'Phone Number', value: person?.phoneNumber },
        { label: 'Schedule Access', value: person?.hasScheduleAccess ? 'Yes' : 'No' },
        { label: 'Send Notifications', value: person?.shouldSendNotification ? 'Yes' : 'No' },
        { label: 'Hourly Rate', value: person?.hourlyRate ? formatDollars(person.hourlyRate) : null },
      ],
    };

    const fields = [...commonFields, ...(roleSpecificFields[role.toLowerCase()] || [])];

    return (
      <Box className={classes.infoGrid}>
        {fields.map((field, index) => (
          <InfoField key={index} label={field.label} value={field.value} type={field.type} />
        ))}
      </Box>
    );
  };

  const renderHeaderStats = () => {
    const entries = monitoringData?.staffMonitoringById?.entries || [];
    const sortedEntries = [...entries].sort((a, b) => {
      return new Date(a.setAt).getTime() - new Date(b.setAt).getTime();
    });

    const firstEntry = sortedEntries[0]?.setAt || null;
    const lastEntry = sortedEntries[sortedEntries.length - 1]?.setAt || null;

    const isStillOnSite = () => {
      if (!lastEntry) return false;

      const lastEntryData = sortedEntries[sortedEntries.length - 1];
      if (!lastEntryData?.room) return false;

      const lastEntryDate = new Date(lastEntry);
      const today = new Date();

      const lastEntryInHospitalTZ = new Date(
        lastEntryDate.toLocaleString('en-US', { timeZone: hospitalTimezone || 'UTC' })
      );
      const todayInHospitalTZ = new Date(today.toLocaleString('en-US', { timeZone: hospitalTimezone || 'UTC' }));

      return lastEntryInHospitalTZ.toDateString() === todayInHospitalTZ.toDateString();
    };

    const effectiveEndTime = isStillOnSite() ? currentTime.toISOString() : lastEntry;
    const cost = calculateCost(person?.hourlyRate, firstEntry, effectiveEndTime);

    return (
      <Box className={classes.headerStats}>
        <Box className={classes.statBox}>
          <Typography className={classes.statLabel}>Arrived at</Typography>
          <Typography className={classes.statValue}>{formatDateTime(firstEntry)}</Typography>
        </Box>
        <Box className={classes.statBox}>
          <Typography className={classes.statLabel}>Stayed for</Typography>
          <Typography className={classes.statValue}>{calculateDuration(firstEntry, effectiveEndTime)}</Typography>
        </Box>
        <Box className={classes.statBox}>
          <Typography className={classes.statLabel}>Left at</Typography>
          <Typography className={classes.statValue}>
            {isStillOnSite() ? 'On Site' : formatDateTime(lastEntry)}
          </Typography>
        </Box>
        <Box className={classes.statBox}>
          <Typography className={classes.statLabel}>Total cost</Typography>
          <Typography className={classes.statValue}>{cost ? formatDollars(cost) : '-'}</Typography>
        </Box>
        <Box className={classes.headerActions}>
          {isStillOnSite() && (
            <Box className={classes.liveIndicator}>
              <div className="pulse" />
              <span>LIVE</span>
            </Box>
          )}
          <Tabs
            value={currentView}
            onChange={(_, newValue) => setCurrentView(newValue)}
            className={classes.customTabs}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab value="timeline" label="Timeline" icon={<TimelineIcon />} />
            <Tab value="piechart" label="Pie Chart" icon={<DonutLargeIcon />} />
            <Tab value="logs" label="Logs" icon={<ListAltIcon />} />
          </Tabs>
          <Button
            variant="contained"
            className={classes.dateButton}
            onClick={e => setDatePickerAnchor(e.currentTarget)}
          >
            {formatDateDisplay(date)}
          </Button>
          <Popover
            open={Boolean(datePickerAnchor)}
            anchorEl={datePickerAnchor}
            onClose={() => setDatePickerAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Calendar
              date={createLocalDate(date)}
              maxDate={new Date()}
              onChange={selectedDate => {
                setDatePickerAnchor(null);
                const organizationId = match.params.organizationId;
                const newDate = formatLocalDate(new Date(selectedDate));
                history.push(`/kiosk/${organizationId}/staff-log/${id}/${role.toLowerCase()}/${newDate}`);
              }}
            />
          </Popover>
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.titleContainer}>
        <IconButton onClick={handleBack} className={classes.backButton}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h1" className={classes.title}>
          {person?.name || '-'}
        </Typography>
      </Box>
      {renderStaffInfo()}
      {renderHeaderStats()}
      {renderTimeline()}
    </Box>
  );
};

export default StaffLogPage;
